<template>
  <div class="relation-menu">
    <item-header type="relation" :label="label" @close="select(null)">
      <template #content>
        <button-item @click="select(ctx.startItem)">
          {{ ctx.startItem?.title }}
        </button-item>
        <div class="text-center pb-1 pt-2">
          <lucide-icon
            :name="ctx.relation.updateType == 'none' ? 'x' : 'arrow-down'"
          />
        </div>
        <button-item @click="select(ctx.endItem)">
          {{ ctx.endItem?.title }}
        </button-item>
      </template>
    </item-header>
    <drawer
      v-model:open="openDrawers.relationDescription"
      :title="$t('labels.general')"
    >
      <div class="column gap-4">
        <input-texteditor
          :value="ctx.relation.description"
          :placeholder="$t('labels.description')"
          :label="$t('labels.description')"
          @update:value="ctx.updateKey('description', $event)"
        />
      </div>
    </drawer>

    <!-- INFLUENCE CURVE -->
    <drawer
      v-model:open="openDrawers.relationCurve"
      :title="$t('labels.modeOfImpact')"
    >
      <div class="column gap-4">
        <section>
          <div class="line-docanchor">
            <label class="label">{{ $t("labels.relationUpdateType") }}</label>
            <doc-anchor :object="ctx.relation" prop="inverse" />
          </div>
          <input-radio
            :label="$t('labels.relationStandard')"
            :data="'not-inverse'"
            v-model:value="impactType"
          />
          <input-radio
            :label="$t('labels.relationInverse')"
            :data="'inverse'"
            v-model:value="impactType"
          />
          <input-radio
            :label="$t('relationUpdateTypes.none')"
            :data="'none'"
            v-model:value="impactType"
          />
        </section>

        <template v-if="ctx.relation.updateType !== 'none'">

          <section
            v-if="targetOnlyRelation"
          >
            <!-- MAX INFLUENCE -->
            <div class="line-docanchor">
              <label class="label">{{ $t("labels.impactStrength") }}</label>
              <doc-anchor :object="ctx.relation" prop="maxInfluence" />
            </div>
            <input-strength
              :strength="curveMaxInfluence"
              :strength-presets="relationStrengthPresets"
              :unit="ctx.endItem?.unit"
              @update:strength="updateMaxInfluence"
            />
          </section>
          <input-number
            v-else
            :value="curveMaxInfluence"
            :label="$t('labels.relationStrength')"
            step="any"
            @update:value="updateMaxInfluence($event)"
          />

          <section
            v-if="
              curveMaxInfluence > 0 ||
              !targetOnlyRelation ||
              curveType == 'formula'
            "
          >
            <div class="line-docanchor">
              <label class="label">{{ $t("labels.influenceCurve") }}</label>
              <doc-anchor :object="ctx.relation" prop="curve" />
            </div>
            <input-curve
              :value="startItemValue"
              :x-min="startItemMinMax.minValue"
              :x-max="startItemMinMax.maxValue"
              :point-list="curvePoints"
              :type="curveType"
              :fixed-start="true"
              :formula="ctx.relation.formula"
              :color-areas-visible="ctx.startItem?.type === 'target' && ctx.endItem?.type === 'target'"
              class="mt-1"
              @change="onGraphChange"
              @update:formula="ctx.updateKey('formula', $event)"
              @hovervalue="updateCurveHover($event)"
            />
            <p>
              {{ $t("labels.xAxis") }}{{ $t(":") }}
              {{
                $t("labels.valueOfX", [
                  ctx.startItem?.title || $t("labels.unnamed")
                ])
              }}<br />
              {{ $t("labels.yAxis") }}{{ $t(":") }}
              {{
                $t("labels.influenceOnX", [
                  ctx.endItem?.title || $t("labels.unnamed")
                ])
              }}
            </p>

            
            <i18n-t
              keypath="texts.influenceCurveExample"
              tag="p"
              scope="global"
            >
              <template #startElement>
                <em>{{ ctx.startItem?.title || $t("labels.unnamed") }}</em>
              </template>
              <template v-if="startItemUnit" #startUnit
                > {{ startItemUnit }}
              </template>
              <template #endElement>
                <em>{{ ctx.endItem?.title || $t("labels.unnamed") }}</em>
              </template>
              <template v-if="ctx.endItem?.unit" #endUnit
                > {{ ctx.endItem?.unit }}
              </template>
              <template #startValueA>
                {{ curveHover.x2 }}
              </template>
              <template #startValueB> {{ curveHover.x }}</template>
              <template #impulseValue>
                {{ $n(curveHover.y - curveHover.y2 || 0, "displaySign") }}
              </template>
            </i18n-t>
          </section>
        </template>
      </div>
    </drawer>

    <!-- DELAY -->
    <drawer
      v-if="ctx.relation.updateType !== 'none'"
      v-model:open="openDrawers.relationDelay"
      :title="$t('labels.delay')"
    >
      <div class="line-docanchor mt-2">
        <div>
          <input-radio
            v-slot="{ id: delayInputId }"
            v-model:value="relationDelayed"
            :data="false"
          >
            <label :for="delayInputId" class="pv-1">
              <strong>{{ $t("labels.noDelay") }}</strong>
              <div class="mt-1">{{ $t("texts.effectInNextRound") }}</div>
            </label>
          </input-radio>

          <input-radio
            v-slot="{ selected, id: delayInputId }"
            v-model:value="relationDelayed"
            :data="true"
          >
            <div v-if="selected" class="pv-1">
              <label :for="delayInputId">
                <strong>{{ $t("labels.delay") }}:</strong>
              </label>
              <div class="line_flex items-center mt-1 gap-1">
                <input-number
                  ref="inputRelationDelayField"
                  style="width: 4rem"
                  :disabled="!selected"
                  :input-class="selected ? [] : ['pointer-events-none']"
                  :value="ctx.relation.delay + 1"
                  :min="2"
                  @update:value="ctx.updateKey('delay', $event - 1)"
                />
                {{ $t(`roundUnits.${roundUnit}`, ctx.relation.delay + 1) }}
              </div>
            </div>
            <label v-else :for="delayInputId" class="pv-1">
              <strong>{{ $t("actions.setDelay") }}</strong>
            </label>
          </input-radio>
        </div>

        <doc-anchor :object="ctx.relation" prop="delay" class="self-start" />
      </div>
    </drawer>

    <drawer
      v-model:open="openDrawers.relationDesign"
      :title="$t('labels.design')"
    >
      <div class="column gap-4">
        <input-switch
          v-model:value="visibleInScene"
          :label="$t('labels.visible')"
          :label-right="true"
        />
      </div>
    </drawer>

    <!-- DEV DATA -->
    <drawer v-if="developerMode" :title="$t('labels.data')" :open="true">
      {{ ctx.relation }}
    </drawer>

    <menu-group class="mt-auto">
      <button
        class="button button_secondary mt-2 mb-2"
        type="button"
        :class="{ button_light: isRelationModeActive }"
        @click="ctx.delete()"
      >
        {{ $t("actions.deleteRelation", 1) }}
      </button>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, nextTick } from "vue";
import { useI18n } from "vue-i18n";

import Drawer from "@core/ui/DrawerBlock.vue";
import MenuGroup from "@core/ui/MenuGroup.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import DocAnchor from "@editor/ui/DocumentationAnchor.vue";

import ButtonItem from "@core/input/ButtonItem.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputTexteditor from "@core/input/TextEditor.vue";
import InputRadio from "@core/input/RadioButton.vue";
import InputSwitch from "@core/input/InputSwitch.vue";

import ItemHeader from "@editor/components/ItemHeader.vue";
import InputCurve from "@editor/items/relation/InputCurve.vue";

import InputStrength from "@editor/items/relation/InputStrength.vue";

import { useRelationStore } from "@editor/items/relation/relations.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { showConfirm } from "@core/ui/alert/alert";
import { useSettingsStore } from "@editor/store/settings.store";
import { useIndexStore } from "../index/indices.store";
import { useRelation } from "./useRelation";
import { useModelViewStore } from "@editor/views/model/modelView.store";
import { useElements } from "../element/elements";
import { useSceneTree } from "../scene/sceneTree";
import { useSceneRelationSettingsStore, type SceneRelationSettingsItem } from "./sceneRelationSettings.store";

import type { GraphPoint } from "./GraphPoint";
import type { CurveFormType, RelationItem } from "./RelationItem";
import type { TargetItem } from "../target/TargetItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { IndexItem } from "../index/IndexItem";

const props = defineProps<{
  relationId: number;
}>();

const { t } = useI18n();

const ctx = useRelation(() => props.relationId);
const relationStore = useRelationStore();
const indexStore = useIndexStore();
const settingsStore = useSettingsStore();
const openDrawers = useDrawerStates();

const inputRelationDelayField = ref<InstanceType<typeof InputNumber>>();

const curveHover = reactive({ x: 0, y: 0, x2: 0, y2: 0 });

const label = computed(() => {
  return t(`relationUpdateTypes.${ctx.relation.updateType}`);
});

// const availableUpdateTypes = computed(() => {
//   const availableUpdateTypes = ["change"];

//   if (ctx.relation.endItemType === "target") {
//     availableUpdateTypes.push("none");
//   } else if (ctx.relation.endItemType === "resource") {
//     // Resources can be changed in any way.
//     return ["change", "none"];
//   } else if (ctx.relation.endItemType === "index") {
//     // Indices can't have incoming relations
//     return [];
//   }

//   return availableUpdateTypes;
// });

const inverted = computed({
  get() {
    return ctx.relation.inverse;
  },
  set(val: boolean) {
    let current = inverted.value;
    if (val === current) return;
    inverse();
  }
});

const impactType = computed({
  get() {
    return ctx.relation.updateType === "none" ? "none" : (ctx.relation.inverse ? "inverse" : "not-inverse");
  },
  set(value: "none" | "inverse" | "not-inverse") {
    if (value == "none") {
      ctx.updateKey("updateType", "none");
    } else {
      const inverted = value == "inverse";
      if (inverted != ctx.relation.inverse) {
        inverse();
      } else if (ctx.relation.updateType == "none") {
        ctx.updateKey("updateType", "change");
      }
    }
  }
});

const curvePoints = computed(() => ctx.relation.curvePoints);

const curveType = computed(() => ctx.relation.curveType);

const relationGraphModel = computed(() => {
  return relationStore.graphModels[props.relationId];
});

const curveMaxInfluence = computed(() => {
  return (
    relationGraphModel.value?.absoluteMaxInfluence ?? ctx.relation.maxInfluence
  );
});

const developerMode = computed(() => {
  return settingsStore.developerMode;
});

const isRelationModeActive = computed(() => {
  return useModelViewStore().impactSourceElement != null;
});

const relationStrengthPresets = computed(() => {
  return settingsStore.relationStrengthPresets;
});

const roundUnit = computed(() => {
  return settingsStore.simulationRoundUnit;
});

const relationDelayed = computed({
  get() {
    return ctx.relation.delay > 0;
  },
  async set(delayed: boolean) {
    if (ctx.relation.delay > 0 && delayed) return;
    if (ctx.relation.delay == 0 && !delayed) return;

    if (delayed) {
      ctx.updateKey("delay", Math.max(1, ctx.relation.delay));
      nextTick(() => {
        inputRelationDelayField.value?.focus();
      });
    } else {
      if (await showConfirm(t("questions.confirmRemovingDelay"))) {
        ctx.updateKey("delay", 0);
      }
    }
  }
});

/**
 * Is the relation only between targets, no resources?
 */
const targetOnlyRelation = computed(() => {
  return (
    ctx.relation.startItemType === "target" &&
    ctx.relation.endItemType === "target"
  );
});

const startItemValue = computed(() => {
  if (!ctx.startItem) return 0;
  if ("value" in ctx.startItem) return ctx.startItem.value;

  return indexStore.values[ctx.startItem.id];
});

const elementValues = computed(() => useElements().elementValues);

const startItemMinMax = computed(() => {
  if (!ctx.startItem) return { minValue: 0, maxValue: 0 };
  return elementValues.value[ctx.startItem.uid];
});

const startItemUnit = computed(() => {
  if (!ctx.startItem) return "";
  if ("unit" in ctx.startItem) return ctx.startItem.unit;

  return "";
});

function onGraphChange(data: { points: GraphPoint[]; type: CurveFormType }) {
  relationStore.updateCurve({
    id: props.relationId,
    curvePoints: data.points,
    curveType: data.type
  });
}

function inverse() {
  return relationStore.invertCurve({
    id: props.relationId
  });
}

function updateMaxInfluence(value: number) {
  const relation = ctx.updateKey("maxInfluence", value);
  relationStore.updatePointBounds({ id: relation.id });
}

function updateCurveHover(newData: {
  x: number;
  y: number;
  x2: number;
  y2: number;
}) {
  curveHover.x = newData.x;
  curveHover.y = newData.y;
  curveHover.x2 = newData.x2;
  curveHover.y2 = newData.y2;
}

function select(
  item: RelationItem | TargetItem | ResourceItem | IndexItem | null
) {
  if (item) {
    return useSelectionStore().select(item);
  } else {
    useSelectionStore().clear();
  }
}

// ==================
// SCENE SETTINGS

const sceneTree = useSceneTree();
const sceneRelationSettingsStore = useSceneRelationSettingsStore();
const sceneRelationSettings = computed(() => {
  return sceneRelationSettingsStore.byScene[sceneTree.activeScene.id]?.[ctx.relation.id];
});
function updateSceneRelationSettings<K extends keyof SceneRelationSettingsItem>(relationId: number,
  key: K,
  value: SceneRelationSettingsItem[K]
) {
  sceneRelationSettingsStore.updateOrCreate({
    relationId,
    sceneId: sceneTree.activeScene.id,
    [key]: value
  });
}

const visibleInScene = computed({
  get() {
    return sceneRelationSettings.value?.visible ?? true;
  },
  set(value: boolean) {
    updateSceneRelationSettings(props.relationId, "visible", value);
  }
});
</script>

<style lang="scss">
.relation-menu {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
</style>
